import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { useEffect, useState } from 'react';
import CurrencyField from '../../benefit/fields/CurrencyField';
import EffectiveDateField from '../common/EffectiveDateField';
import { FormValidationContext } from '../../benefit/common/FormValidationContext';
import { useHydratedForm } from '../hooks/useHydratedForm';
import { useBootstrapValidation } from '../hooks/userBootstrapValidation';
import { useUI } from '../../../context/UIContext';
import { calculatorService } from '../../../services/calculatorService';
import { hasMeaningfulChange } from '../../../utils/hasMeaningfulChange';

const mapFplSmiToForm = (fplSmi) => ({
  id: fplSmi.id,
  householdSize: fplSmi.householdSize ?? '',
  fpl: fplSmi.fpl ?? '',
  smi: fplSmi.smi ?? '',
  effectiveDate: fplSmi.effectiveDate ?? '',
});

const MEANINGFUL_FIELDS = ['fpl', 'smi', 'effectiveDate'];

const EditFplSmiModal = ({ fplSmi, onSave, onClose }) => {

  const [form, setForm] = useHydratedForm(fplSmi, mapFplSmiToForm);

  const { validated, validateForm } = useBootstrapValidation();
  const [customError, setCustomError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState('');
  const { showLoading, hideLoading, showError, showSuccess } = useUI();

  if (!fplSmi) return null;

  const handleSubmit = async (e) => {
    const formEl = e.currentTarget;
    e.preventDefault();

    if (!hasMeaningfulChange(fplSmi, form, MEANINGFUL_FIELDS)) {
      setCustomError(
        'At least one value must be changed before saving.'
      );
      return; 
    }

    if (!validateForm(formEl)) return;

    setIsSubmitting(true);
    showLoading('Saving FPL & SMI configuration…');

    try {
      const response = await calculatorService.saveFplSmi(form);
      setIsSubmitting(false);

      if (response.success) {
        onSave(response.data);
        onClose();
        showSuccess(`FPL & SMI configuration for Household Size ${fplSmi.householdSize} saved.`)
      } else {
        showError(
          response.error || 'Failed to save tier configuration.'
        );
      }
    } finally {
      setIsSubmitting(false);
      hideLoading();
    }
  };

  return (
    <Modal show onHide={onClose} size="lg" centered>
      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        <FormValidationContext.Provider value={validated}>
          <Modal.Header closeButton>
            <Modal.Title>
              Edit FPL / SMI for Household Size {form.householdSize}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
                <Row className="mb-3">
                  <Col>
                    <CurrencyField
                      value={form.fpl}
                      scale={0}
                      fieldName="Federal Poverty Level (FPL)"
                      required
                        onChange={(value) =>
                          setForm({ ...form, fpl: value })
                        }
                      isSubmitting={isSubmitting}
                    />
                  </Col>
                  <Col>
                    <CurrencyField
                      value={form.smi}
                      scale={0}
                      fieldName="State Median Income (SMI)"
                      required
                      onChange={(value) =>
                        setForm({ ...form, smi: value })
                      }
                      isSubmitting={isSubmitting}
                    />
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <EffectiveDateField
                      value={form.effectiveDate}
                      onChange={(v) => setForm({ ...form, effectiveDate: v })}
                      isSubmitting={isSubmitting}
                    />
                  </Col>
                </Row>

                {customError && (
                  <div className="text-center text-danger mt-3">
                    {customError}
                  </div>
                )}
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Save
            </Button>
          </Modal.Footer>
        </FormValidationContext.Provider>
      </Form>
    </Modal>
  );
};

export default EditFplSmiModal;
